/** Moon phase maths over precomputed principal phase instants (see seed data `lunar_phases.json`). */

export const PRINCIPAL_PHASES = [
  "new_moon",
  "first_quarter",
  "full_moon",
  "third_quarter",
] as const;
export type PrincipalPhase = (typeof PRINCIPAL_PHASES)[number];

export const PHASES = [
  "new_moon",
  "waxing_crescent",
  "first_quarter",
  "waxing_gibbous",
  "full_moon",
  "waning_gibbous",
  "third_quarter",
  "waning_crescent",
] as const;
export type MoonPhase = (typeof PHASES)[number];

export interface LunarEvent {
  type: PrincipalPhase;
  at_utc: string;
  moon_sign: string;
  traditional_name?: string;
  is_blue_moon?: boolean;
  is_supermoon?: boolean;
}

export interface MoonState {
  phase: MoonPhase;
  /** 0..1, fraction of the synodic month since the last new moon. */
  age: number;
  /** 0..1, approximate illuminated fraction of the disc. */
  illumination: number;
  /** The ~3 days before a new moon, favoured for rest and banishing. */
  isDarkMoon: boolean;
  previous: LunarEvent;
  next: LunarEvent;
}

const DAY_MS = 86_400_000;
/** A principal phase "lasts" this long either side of its exact instant for display purposes. */
export const PRINCIPAL_WINDOW_MS = DAY_MS;
const DARK_MOON_MS = 3 * DAY_MS;

const BETWEEN: Record<PrincipalPhase, MoonPhase> = {
  new_moon: "waxing_crescent",
  first_quarter: "waxing_gibbous",
  full_moon: "waning_gibbous",
  third_quarter: "waning_crescent",
};

const time = (e: LunarEvent) => Date.parse(e.at_utc);

/** Index of the last event at or before `t` in a time-sorted list, or -1. */
function lastIndexAtOrBefore(events: LunarEvent[], t: number): number {
  let lo = 0;
  let hi = events.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (time(events[mid]!) <= t) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return found;
}

/**
 * Moon state at `date`, or null when `date` falls outside the event data.
 * `events` must be sorted by time (the seed data is).
 */
export function moonStateAt(date: Date, events: LunarEvent[]): MoonState | null {
  const t = date.getTime();
  const i = lastIndexAtOrBefore(events, t);
  if (i < 0 || i >= events.length - 1) return null;
  const previous = events[i]!;
  const next = events[i + 1]!;

  const newIdx = lastIndexOfType(events, i, "new_moon");
  const nextNewIdx = events.findIndex((e, k) => k > i && e.type === "new_moon");
  if (newIdx < 0 || nextNewIdx < 0) return null;
  const lastNew = time(events[newIdx]!);
  const nextNew = time(events[nextNewIdx]!);
  const age = (t - lastNew) / (nextNew - lastNew);

  let phase: MoonPhase;
  if (t - time(previous) <= PRINCIPAL_WINDOW_MS) phase = previous.type;
  else if (time(next) - t <= PRINCIPAL_WINDOW_MS) phase = next.type;
  else phase = BETWEEN[previous.type];

  return {
    phase,
    age,
    illumination: (1 - Math.cos(2 * Math.PI * age)) / 2,
    isDarkMoon: phase === "waning_crescent" && nextNew - t <= DARK_MOON_MS,
    previous,
    next,
  };
}

function lastIndexOfType(events: LunarEvent[], from: number, type: PrincipalPhase): number {
  for (let k = from; k >= 0; k--) if (events[k]!.type === type) return k;
  return -1;
}

/** The next event of `type` strictly after `date`, if known. */
export function nextPhaseEvent(
  date: Date,
  events: LunarEvent[],
  type: PrincipalPhase,
): LunarEvent | undefined {
  const t = date.getTime();
  return events.find((e) => e.type === type && time(e) > t);
}

const LABELS: Record<MoonPhase, string> = {
  new_moon: "New Moon",
  waxing_crescent: "Waxing Crescent",
  first_quarter: "First Quarter",
  waxing_gibbous: "Waxing Gibbous",
  full_moon: "Full Moon",
  waning_gibbous: "Waning Gibbous",
  third_quarter: "Last Quarter",
  waning_crescent: "Waning Crescent",
};

const GLYPHS: Record<MoonPhase, string> = {
  new_moon: "🌑",
  waxing_crescent: "🌒",
  first_quarter: "🌓",
  waxing_gibbous: "🌔",
  full_moon: "🌕",
  waning_gibbous: "🌖",
  third_quarter: "🌗",
  waning_crescent: "🌘",
};

export function phaseLabel(phase: MoonPhase): string {
  return LABELS[phase];
}

/** Moon emoji for a phase. In the southern hemisphere the lit side is mirrored. */
export function phaseGlyph(
  phase: MoonPhase,
  hemisphere: "northern" | "southern" = "northern",
): string {
  if (hemisphere === "northern") return GLYPHS[phase];
  const mirror: Partial<Record<MoonPhase, MoonPhase>> = {
    waxing_crescent: "waning_crescent",
    waning_crescent: "waxing_crescent",
    first_quarter: "third_quarter",
    third_quarter: "first_quarter",
    waxing_gibbous: "waning_gibbous",
    waning_gibbous: "waxing_gibbous",
  };
  return GLYPHS[mirror[phase] ?? phase];
}

/** Waxing phases build energy; waning phases release it. */
export function isWaxing(phase: MoonPhase): boolean {
  return phase === "waxing_crescent" || phase === "first_quarter" || phase === "waxing_gibbous";
}

const SIGNS = [
  "aries",
  "taurus",
  "gemini",
  "cancer",
  "leo",
  "virgo",
  "libra",
  "scorpio",
  "sagittarius",
  "capricorn",
  "aquarius",
  "pisces",
] as const;

/**
 * The moon's geocentric ecliptic longitude in degrees (0..360), from the main terms of
 * Meeus' lunar theory. Accurate to well under a degree, plenty for which sign it's in.
 */
export function moonLongitude(date: Date): number {
  const d = date.getTime() / 86_400_000 + 2440587.5 - 2451545.0;
  const rad = Math.PI / 180;
  const L = 218.3164477 + 13.17639648 * d;
  const D = (297.8501921 + 12.19074912 * d) * rad;
  const M = (357.5291092 + 0.98560028 * d) * rad;
  const Mp = (134.9633964 + 13.06499295 * d) * rad;
  const F = (93.272095 + 13.22935024 * d) * rad;
  const lon =
    L +
    6.289 * Math.sin(Mp) +
    1.274 * Math.sin(2 * D - Mp) +
    0.658 * Math.sin(2 * D) +
    0.214 * Math.sin(2 * Mp) -
    0.186 * Math.sin(M) -
    0.114 * Math.sin(2 * F);
  return ((lon % 360) + 360) % 360;
}

/** Tropical zodiac sign the moon is in at `date`. */
export function moonSignAt(date: Date): string {
  return SIGNS[Math.floor(moonLongitude(date) / 30)]!;
}
